import { useInfiniteQuery } from '@tanstack/react-query';
import { ApiConfig } from '@/config/api.config';
import { UrlHelper } from '@/utils/UrlHelper';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';
import { SuperAdminDataLookupListResponseDto } from '@/features/superAdmin/features/data/types/superAdmin-data.types';

const DEFAULT_PAGE_SIZE = 20;

const getNextPageParam = (
    lastPage: SuperAdminDataLookupListResponseDto,
    allPages: SuperAdminDataLookupListResponseDto[],
) => {
    const totalLoaded = allPages.flatMap((page) => page.items).length;
    return totalLoaded < lastPage.totalCount ? totalLoaded : undefined;
};

export const useSuperAdminDataOwnersLookupInfiniteApi = (
    tenantId: string | null | undefined,
    searchText?: string,
) => {
    const axiosInstance = useAxiosAuth();

    const query = useInfiniteQuery({
        queryKey: [
            ApiConfig.queries.superAdmin.data.lookups.queryKeys.owners,
            'infinite',
            tenantId,
            searchText,
            DEFAULT_PAGE_SIZE,
        ],
        queryFn: async ({ pageParam }): Promise<SuperAdminDataLookupListResponseDto> => {
            const url = UrlHelper.buildUrlWithQueryParams(
                ApiConfig.queries.superAdmin.data.lookups.endpoints.owners,
                {
                    tenantId,
                    searchText: searchText?.trim() || undefined,
                    take: DEFAULT_PAGE_SIZE,
                    skip: pageParam,
                },
            );
            const response = await axiosInstance.get<SuperAdminDataLookupListResponseDto>(url);
            return response.data;
        },
        getNextPageParam,
        initialPageParam: 0,
        enabled: Boolean(tenantId),
    });

    return {
        items: query.data?.pages.flatMap((page) => page.items ?? []) ?? [],
        fetchNextPage: query.fetchNextPage,
        hasNextPage: query.hasNextPage,
        isFetchingNextPage: query.isFetchingNextPage,
        isLoading: query.isLoading,
    };
};

export const useSuperAdminDataHotelsLookupInfiniteApi = (
    tenantId: string | null | undefined,
    searchText?: string,
) => {
    const axiosInstance = useAxiosAuth();

    const query = useInfiniteQuery({
        queryKey: [
            ApiConfig.queries.superAdmin.data.lookups.queryKeys.hotels,
            'infinite',
            tenantId,
            searchText,
            DEFAULT_PAGE_SIZE,
        ],
        queryFn: async ({ pageParam }): Promise<SuperAdminDataLookupListResponseDto> => {
            const url = UrlHelper.buildUrlWithQueryParams(
                ApiConfig.queries.superAdmin.data.lookups.endpoints.hotels,
                {
                    tenantId,
                    searchText: searchText?.trim() || undefined,
                    take: DEFAULT_PAGE_SIZE,
                    skip: pageParam,
                },
            );
            const response = await axiosInstance.get<SuperAdminDataLookupListResponseDto>(url);
            return response.data;
        },
        getNextPageParam,
        initialPageParam: 0,
        enabled: Boolean(tenantId),
    });

    return {
        items: query.data?.pages.flatMap((page) => page.items ?? []) ?? [],
        fetchNextPage: query.fetchNextPage,
        hasNextPage: query.hasNextPage,
        isFetchingNextPage: query.isFetchingNextPage,
        isLoading: query.isLoading,
    };
};

export const useSuperAdminDataApartmentsLookupInfiniteApi = (
    tenantId: string | null | undefined,
    searchText?: string,
    hotelId?: string | null,
) => {
    const axiosInstance = useAxiosAuth();

    const query = useInfiniteQuery({
        queryKey: [
            ApiConfig.queries.superAdmin.data.lookups.queryKeys.apartments,
            'infinite',
            tenantId,
            searchText,
            hotelId,
            DEFAULT_PAGE_SIZE,
        ],
        queryFn: async ({ pageParam }): Promise<SuperAdminDataLookupListResponseDto> => {
            const url = UrlHelper.buildUrlWithQueryParams(
                ApiConfig.queries.superAdmin.data.lookups.endpoints.apartments,
                {
                    tenantId,
                    searchText: searchText?.trim() || undefined,
                    hotelId: hotelId ?? undefined,
                    take: DEFAULT_PAGE_SIZE,
                    skip: pageParam,
                },
            );
            const response = await axiosInstance.get<SuperAdminDataLookupListResponseDto>(url);
            return response.data;
        },
        getNextPageParam,
        initialPageParam: 0,
        enabled: Boolean(tenantId),
    });

    return {
        items: query.data?.pages.flatMap((page) => page.items ?? []) ?? [],
        fetchNextPage: query.fetchNextPage,
        hasNextPage: query.hasNextPage,
        isFetchingNextPage: query.isFetchingNextPage,
        isLoading: query.isLoading,
    };
};
